/**
 * Money is stored as **milliunits**: integer thousandths of a currency unit.
 * $1.00 === 1000mu. This matches YNAB's own API, which matters for two reasons:
 * floats never touch a balance, and splitting a transaction three ways loses
 * nothing to rounding (a cent-based store cannot represent $10.00 / 3).
 */

/**
 * Branded with a string-literal property rather than a module-local
 * `unique symbol`: a symbol brand cannot be *named* by the compiler when an
 * inferred type crosses a package boundary, which breaks Hono's RPC type export
 * with "cannot be named" (TS4023). The property is phantom — it never exists at
 * runtime.
 */
export type Milliunits = number & { readonly __brand: 'Milliunits' }

export const ZERO = 0 as Milliunits
export const MILLIUNITS_PER_UNIT = 1000

/** Trusts that `n` is already an integer count of milliunits. */
export function mu(n: number): Milliunits {
  if (!Number.isInteger(n)) {
    throw new RangeError(`Milliunits must be an integer, received ${n}`)
  }
  if (!Number.isSafeInteger(n)) {
    throw new RangeError(`Milliunits out of safe integer range: ${n}`)
  }
  // Normalise -0 to 0. `neg(ZERO)` yields -0, and Object.is(-0, 0) is false, so
  // an un-normalised -0 makes `carryOut === 0` checks and JSON round-trips lie.
  return (n === 0 ? 0 : n) as Milliunits
}

/** Converts a major-unit amount (dollars) to milliunits, rounding half away from zero. */
export function fromUnits(units: number): Milliunits {
  if (!Number.isFinite(units)) {
    throw new RangeError(`Cannot convert non-finite ${units} to milliunits`)
  }
  const scaled = units * MILLIUNITS_PER_UNIT
  // Math.round(-0.5) is -0, which rounds *towards* zero and would make
  // -$0.0005 and +$0.0005 disagree. Round the magnitude, reapply the sign.
  const rounded = Math.sign(scaled) * Math.round(Math.abs(scaled))
  return mu(rounded === 0 ? 0 : rounded)
}

export function toUnits(amount: Milliunits): number {
  return amount / MILLIUNITS_PER_UNIT
}

export function add(...amounts: Milliunits[]): Milliunits {
  let total = 0
  for (const a of amounts) total += a
  return mu(total)
}

export function sub(a: Milliunits, b: Milliunits): Milliunits {
  return mu(a - b)
}

export function neg(a: Milliunits): Milliunits {
  return mu(-a)
}

export function abs(a: Milliunits): Milliunits {
  return mu(Math.abs(a))
}

export function sum(amounts: Iterable<Milliunits>): Milliunits {
  let total = 0
  for (const a of amounts) total += a
  return mu(total)
}

export function min(a: Milliunits, b: Milliunits): Milliunits {
  return a <= b ? a : b
}

export function max(a: Milliunits, b: Milliunits): Milliunits {
  return a >= b ? a : b
}

export function clamp(value: Milliunits, lower: Milliunits, upper: Milliunits): Milliunits {
  if (lower > upper) throw new RangeError('clamp lower bound exceeds upper bound')
  return max(lower, min(value, upper))
}

/** Negative part of a value, as a positive magnitude. `overspend(-500) === 500`. */
export function negativePart(a: Milliunits): Milliunits {
  return a < 0 ? mu(-a) : ZERO
}

export function positivePart(a: Milliunits): Milliunits {
  return a > 0 ? a : ZERO
}

export function isZero(a: Milliunits): boolean {
  return a === 0
}

/**
 * The amount as a reader of it sees it: snapped to the nearest cent.
 *
 * Milliunits are a tenth of a cent, and legitimately land between them —
 * `allocate` leaves remainders by design, and so does splitting a credit-card
 * payment. Everything downstream then prints two decimals, so a residue under half
 * a cent is invisible in the figure while still being a non-zero number to any code
 * that tests its sign.
 *
 * That gap is not cosmetic. Ready to Assign sat at minus three milliunits and the
 * header showed "$0.00" under a banner reading "assigned more than you have" — the
 * figure and the label disagreeing because one had been rounded for display and the
 * other had not. Round once, then decide and display from the same value, so they
 * cannot come apart.
 *
 * Not for arithmetic on stored amounts: rounding money you are about to keep loses
 * the remainder that `allocate` was careful to preserve.
 */
export function roundToCents(a: Milliunits): Milliunits {
  const rounded = Math.round(a / 10) * 10
  // `Math.round(-0.3)` is `-0`, and a money amount of negative zero is a trap
  // waiting for whichever comparison notices: `Object.is(-0, 0)` is false, and it
  // survives a round trip through anything that preserves the sign bit. `=== 0` is
  // true of both, so this normalises it away.
  return (rounded === 0 ? 0 : rounded) as Milliunits
}

/**
 * Splits `total` into `weights.length` parts proportional to `weights`, using the
 * largest-remainder method so the parts always sum back to exactly `total`.
 * Used for split transactions and for distributing a lump sum across categories.
 */
export function allocate(total: Milliunits, weights: readonly number[]): Milliunits[] {
  if (weights.length === 0) throw new RangeError('allocate requires at least one weight')
  if (weights.some((w) => w < 0)) throw new RangeError('allocate weights must be non-negative')

  const weightTotal = weights.reduce((acc, w) => acc + w, 0)
  if (weightTotal === 0) {
    // Degenerate case: no weight anywhere. Put everything in the first slot.
    return weights.map((_, i) => (i === 0 ? total : ZERO))
  }

  const exact = weights.map((w) => (total * w) / weightTotal)
  // Truncate toward zero so the leftover carries the same sign as `total`.
  const floors = exact.map((v) => Math.trunc(v))
  let remainder = total - floors.reduce((acc, v) => acc + v, 0)

  // Hand out the remaining units to the largest fractional parts first.
  const order = exact
    .map((v, i) => ({ i, frac: Math.abs(v - Math.trunc(v)) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i)

  const step = remainder < 0 ? -1 : 1
  const result = [...floors]
  for (const { i } of order) {
    if (remainder === 0) break
    result[i] = (result[i] as number) + step
    remainder -= step
  }

  return result.map((v) => mu(v))
}

export interface FormatOptions {
  /** ISO 4217 code. Defaults to NZD. */
  currency?: string
  locale?: string
  /** Render as `1,234` instead of `1,234.00`. */
  hideDecimals?: boolean
  /** Render zero as an em dash, the way ledgers usually do. */
  dashForZero?: boolean
}

export function format(amount: Milliunits, options: FormatOptions = {}): string {
  const { currency = 'NZD', locale = 'en-NZ', hideDecimals = false, dashForZero = false } = options

  if (dashForZero && amount === 0) return '—'

  const fractionDigits = hideDecimals ? 0 : 2
  const units = toUnits(amount)

  /*
   * Never a signed zero.
   *
   * Milliunits are three decimal places and this prints two, so anything under half
   * a cent rounds to zero — and `Intl` keeps the sign while doing it, giving
   * "-$0.00". Ready to Assign showed exactly that after a three-milliunit
   * over-assignment: a pill reading "assigned more than you have" above a figure of
   * nothing, which is unreadable rather than merely untidy. `hideDecimals` makes it
   * worse, swallowing anything under fifty cents into "-$0".
   *
   * The residue is a real quantity and this does not pretend otherwise anywhere it
   * can be acted on — it is `!== 0` to every caller that compares. This is about
   * what a minus sign in front of a zero communicates, which is nothing true.
   */
  const scale = 10 ** fractionDigits
  const printable = Math.round(units * scale) === 0 ? 0 : units

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(printable)
}

const PARSE_ALLOWED = /^[\d\s,.'+\-*/()$]+$/
const ARITHMETIC = /[+\-*/()]/

/**
 * Parses amounts the way a budgeting app should: tolerant of currency symbols,
 * thousands separators and a leading/trailing minus, and accepting small
 * arithmetic expressions (`12.50+8`, `40/3`) because entering a split receipt
 * without a calculator is one of the things that makes YNAB pleasant.
 *
 * Returns null on anything it cannot read, so callers can show a field error
 * rather than silently booking a wrong number.
 *
 * **Never returns a fraction of a cent**, and treats the two ways of asking for one
 * differently, because they mean different things.
 *
 * A *literal* with more than two decimals is refused. "49.165" is not an amount
 * anybody can pay or be charged, so it is a typo, and rounding a typo silently
 * books a number nobody typed. Callers already handle null by leaving the field
 * alone.
 *
 * An *expression* is rounded instead. "40/3" is a request to divide forty by three,
 * and the answer to it is $13.33 — refusing the arithmetic this function exists to
 * offer would be perverse. So the division is honoured and its result made payable.
 */
export function parse(input: string): Milliunits | null {
  const raw = input.trim()
  if (raw === '') return null
  if (!PARSE_ALLOWED.test(raw)) return null

  // Strip currency symbols, spaces and thousands separators. Apostrophes are a
  // thousands separator in some locales; commas we treat the same way because a
  // decimal comma plus an expression is ambiguous.
  const cleaned = raw.replace(/[$\s,']/g, '')
  if (cleaned === '' || cleaned === '-' || cleaned === '+') return null

  // Trailing-minus accounting notation: "12.50-" means negative.
  const trailingMinus = /^([^-].*)-$/.exec(cleaned)
  const normalised = trailingMinus ? `-${trailingMinus[1]}` : cleaned

  if (ARITHMETIC.test(normalised.slice(1))) {
    const evaluated = evaluateExpression(normalised)
    if (evaluated === null || !Number.isFinite(evaluated)) return null
    // Rounded, not refused: the caller asked for a division and a third of $40 has
    // to become an amount that can actually leave a bank account.
    return roundToCents(fromUnits(evaluated))
  }

  // A literal carrying more than two decimals is refused rather than rounded. It is
  // a typo, not a calculation, and quietly turning "49.165" into $49.17 books a
  // figure the person never typed — which is how a third of a cent came to sit
  // against an envelope in the first place.
  const fraction = /\.(\d*)$/.exec(normalised)
  if (fraction && (fraction[1] as string).length > 2) return null

  const value = Number(normalised)
  if (!Number.isFinite(value)) return null
  return fromUnits(value)
}

/** An entry that opens with an operator is a change to what is already there. */
const RELATIVE = /^[+\-*/]/

/**
 * `parse`, but an entry opening with an operator adjusts an existing amount.
 *
 * Typing `+50` into a field showing 150 means 200, `/2` means 75, `*2` means 300.
 * It is how you actually think at a budget: not "this envelope should hold 200" but
 * "put another fifty in". Anything not opening with an operator is absolute and goes
 * straight to `parse`, so `200` still sets 200 and `2+3*4` still means 14.
 *
 * **A leading minus subtracts here, where `parse` reads it as a sign.** That is the
 * one place the two disagree, and it is deliberate: in a field that already shows
 * 150, `-100` can only sensibly mean take a hundred off. Which is why this is a
 * separate function rather than a change to `parse` — in a transaction amount a
 * leading minus means money out, and breaking that to gain this would be a poor
 * trade. Note the two agree whenever the base is zero, since `0 - 100` is `-100`,
 * so an empty envelope behaves identically either way.
 *
 * The base is spliced in parenthesised and handed to the same evaluator, so
 * precedence, rounding and the refusal to touch `eval` are all inherited rather
 * than reimplemented. It goes down `parse`'s *expression* branch, which rounds to
 * cents rather than refusing — right for this case, because the base can carry
 * sub-cent precision that `allocate` produced and a person adding fifty dollars to
 * it should not be told their entry is a typo.
 */
export function parseRelative(input: string, base: Milliunits): Milliunits | null {
  const raw = input.trim()
  if (raw === '') return null
  if (!RELATIVE.test(raw)) return parse(raw)
  return parse(`(${toUnits(base)})${raw}`)
}

/**
 * Evaluates `+ - * / ( )` over decimals via shunting-yard. Deliberately not
 * `eval`/`new Function`: this runs on user input and must not be able to reach
 * anything but arithmetic.
 */
function evaluateExpression(expr: string): number | null {
  type Token = { kind: 'num'; value: number } | { kind: 'op'; value: string }
  const tokens: Token[] = []

  let i = 0
  let expectOperand = true
  while (i < expr.length) {
    const ch = expr[i] as string

    if (/[\d.]/.test(ch)) {
      let j = i
      while (j < expr.length && /[\d.]/.test(expr[j] as string)) j++
      const numText = expr.slice(i, j)
      if ((numText.match(/\./g)?.length ?? 0) > 1) return null
      const parsed = Number(numText)
      if (!Number.isFinite(parsed)) return null
      tokens.push({ kind: 'num', value: parsed })
      expectOperand = false
      i = j
      continue
    }

    if (ch === '(' || ch === ')') {
      tokens.push({ kind: 'op', value: ch })
      expectOperand = ch === '('
      i++
      continue
    }

    if ('+-*/'.includes(ch)) {
      if (expectOperand) {
        // Unary sign: fold into an explicit 0 - x / 0 + x.
        if (ch === '-' || ch === '+') {
          tokens.push({ kind: 'num', value: 0 }, { kind: 'op', value: ch })
          expectOperand = true
          i++
          continue
        }
        return null
      }
      tokens.push({ kind: 'op', value: ch })
      expectOperand = true
      i++
      continue
    }

    return null
  }

  if (expectOperand) return null

  const precedence: Record<string, number> = { '+': 1, '-': 1, '*': 2, '/': 2 }
  const output: Token[] = []
  const ops: string[] = []

  for (const token of tokens) {
    if (token.kind === 'num') {
      output.push(token)
      continue
    }
    if (token.value === '(') {
      ops.push(token.value)
      continue
    }
    if (token.value === ')') {
      let matched = false
      while (ops.length > 0) {
        const top = ops.pop() as string
        if (top === '(') {
          matched = true
          break
        }
        output.push({ kind: 'op', value: top })
      }
      if (!matched) return null
      continue
    }
    while (ops.length > 0) {
      const top = ops[ops.length - 1] as string
      if (top === '(') break
      if ((precedence[top] as number) < (precedence[token.value] as number)) break
      output.push({ kind: 'op', value: ops.pop() as string })
    }
    ops.push(token.value)
  }
  while (ops.length > 0) {
    const top = ops.pop() as string
    if (top === '(') return null
    output.push({ kind: 'op', value: top })
  }

  const stack: number[] = []
  for (const token of output) {
    if (token.kind === 'num') {
      stack.push(token.value)
      continue
    }
    const b = stack.pop()
    const a = stack.pop()
    if (a === undefined || b === undefined) return null
    switch (token.value) {
      case '+':
        stack.push(a + b)
        break
      case '-':
        stack.push(a - b)
        break
      case '*':
        stack.push(a * b)
        break
      case '/':
        if (b === 0) return null
        stack.push(a / b)
        break
      default:
        return null
    }
  }

  if (stack.length !== 1) return null
  return stack[0] as number
}
